// Path-based SPA router

export class Router {
    constructor() {
        /** @type {Array<{pattern: string, regex: RegExp, paramNames: string[], name: string, meta: object}>} */
        this.routes = [];
        /** @type {string|null} */
        this.currentRoute = null;
        /** @type {{[key: string]: string}} */
        this.params = {};
        /** @type {object} */
        this.meta = {};
        /** @type {Set<Function>} */
        this.listeners = new Set();

        window.addEventListener('popstate', () => this.handleRoute());

        document.addEventListener('click', (e) => {
            const link = /** @type {HTMLElement} */ (e.target).closest('a');
            if (!link) return;

            const href = link.getAttribute('href');
            if (!href) return;

            if (link.target === '_blank' ||
                link.hasAttribute('download') ||
                href.startsWith('http') ||
                href.startsWith('//') ||
                href.startsWith('/api/')) {
                return;
            }

            if (href.startsWith('/')) {
                e.preventDefault();
                this.navigate(href);
            }
        });
    }

    addRoute(pattern, name, meta = {}) {
        const paramNames = [];
        const regexPattern = pattern.replace(/:(\w+)/g, (_, paramName) => {
            paramNames.push(paramName);
            return '([^/]+)';
        });
        const regex = new RegExp(`^${regexPattern}$`);
        this.routes.push({ pattern, regex, paramNames, name, meta });
    }

    navigate(path) {
        if (!path.startsWith('/')) {
            path = '/' + path;
        }
        window.history.pushState({}, '', path);
        this.handleRoute();
    }

    getPath() {
        const path = window.location.pathname;
        return path || '/';
    }

    getQuery() {
        return new URLSearchParams(window.location.search);
    }

    handleRoute() {
        const path = this.getPath();
        this.params = {};
        this.meta = {};

        for (const route of this.routes) {
            const match = path.match(route.regex);
            if (match) {
                route.paramNames.forEach((name, index) => {
                    const value = match[index + 1];
                    if (value) this.params[name] = value;
                });
                this.currentRoute = route.name;
                this.meta = route.meta;
                this.notify();
                return;
            }
        }

        this.currentRoute = null;
        this.notify();
    }

    subscribe(fn) {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
    }

    notify() {
        const state = {
            route: this.currentRoute,
            params: this.params,
            path: this.getPath(),
            meta: this.meta,
        };
        this.listeners.forEach(fn => fn(state));
    }

    match() {
        const path = this.getPath();
        for (const route of this.routes) {
            const matchResult = path.match(route.regex);
            if (matchResult) {
                const params = {};
                route.paramNames.forEach((name, index) => {
                    const value = matchResult[index + 1];
                    if (value) params[name] = value;
                });
                return { path: route.pattern, params, meta: route.meta, name: route.name };
            }
        }
        return null;
    }
}
